import { rem, Skeleton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useForm, zodResolver } from '@mantine/form';
import { ProposalEntity } from '@modules/clients/entities/client/api/dto/clientProposal.dto';
import { useContext } from 'react';
import {
  Button, Flex, Text
} from '@huspy/briks-web';
import { useNavigate } from '@tanstack/react-router';
import useGetProposalById from '@modules/proposals/api/query/useGetProposalById';
import { TYPE_OF_TRANSACTION } from '@modules/opportunities/entities/opportunity/const';
import { clearAmplitudeEventContext } from '@shared/analytics/amplitude';
import {
  ProposalCreationBankProductsRoute,
  ProposalCreationClientAndMortgageRoute
} from '@modules/proposals/presentation/routes';
import useUpdateProposal from '@modules/proposals/api/mutation/useUpdateProposal';
import { theme } from '@huspy/forge';
import { ClientDetails } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/components/ClientDetails';
import { PropertyDetails } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/components/PropertyDetails';
import { ProposalCreationClientAndMortgageForm } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/types';
import { MortgageDetails } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/components/MortgageDetails';
import { mapProposalClientDetailsFormToRequest } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/mappers/mapProposalClientDetailsFormToRequest';
import { mapMortgageDetailsFormToRequest } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/mappers/mapMortgageDetailsFormToRequest';
import { mapProposalEntityToLegacyProposalEntity } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/mappers/mapProposalEntityToLegacyProposalEntity';
import { ProposalCreationContext } from '@modules/proposals/presentation/v2/pages/ProposalCreation/context';
import { clientAndMortgageUAESchema } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/validators';
import { mapPropertyDetailsFormToRequest } from '@modules/proposals/presentation/v2/pages/ProposalCreation/views/ClientAndMortgage/mappers/mapPropertyDetailsFormToRequest';

export const BUYOUT_TRANSACTION_TYPES = [
  TYPE_OF_TRANSACTION.buyoutEquity,
  TYPE_OF_TRANSACTION.buyout,
  TYPE_OF_TRANSACTION.equity
] as string[];

export const EQUITY_TRANSACTION_TYPES = [
  TYPE_OF_TRANSACTION.buyoutEquity,
  TYPE_OF_TRANSACTION.equity
] as string[];

const calculateOutstandingLoanAmount = (equityReleaseAmount: number, loanValue: number) => {
  if (equityReleaseAmount > 0) {
    return +loanValue - +equityReleaseAmount;
  }
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const formInitialValues = (data?: ProposalEntity & { client_external_id?: string }) => ({
  client_details: {
    first_name: data?.first_name ?? '',
    last_name: data?.last_name ?? '',
    employment_status: data?.employment_status ?? '',
    citizen_status: data?.residence_status ?? '',
    ...(data?.client_external_id && { client_external_id: data?.client_external_id }),
  },
  property_details: {
    state: data?.property_emirate ?? '',
    is_difc: data?.property_location === 'DIFC' ? 'true' : 'false',
    property_location: data?.property_location ?? '',
    property_status: data?.property_status ?? '',
  },
  mortgage_details: {
    transaction_type: data?.transaction_type ?? '',
    mortgage_type: data?.mortgage_type ?? '',
    existing_mortgage_type: data?.existing_mortgage_type ?? '',
    property_value: data?.property_value ?? '',
    loan_value: data?.loan_amount ?? '',
    ltv: data?.ltv ?? '',
    downpayment_amount: data?.downpayment_amount ?? '',
    down_payment: data?.down_payment ?? '',
    equity_release_amount: data?.equity_release_amount ?? '',
    outstanding_loan_amount: data?.equity_release_amount && data?.loan_amount
      ? calculateOutstandingLoanAmount(data?.equity_release_amount, data?.loan_amount)
      : '',
    years: data?.mortgage_length ? String(Math.floor(Number(data?.mortgage_length) / 12)) : '',
    months: data?.mortgage_length ? String(data.mortgage_length % 12) : '0',
    is_real_estate_fee_financed: !!data?.is_real_estate_fee_financed,
    is_land_department_fee_financed: !!data?.is_land_department_fee_financed,
  },
});

type ProposalDetailsFormType = ReturnType<typeof useForm<ProposalCreationClientAndMortgageForm>>;

export const ProposalCreationClientAndMortgage = () => {
  const { id } = ProposalCreationClientAndMortgageRoute.useParams();
  const navigate = useNavigate({ from: ProposalCreationClientAndMortgageRoute.to });

  const { data, isLoading } = useGetProposalById(id, mapProposalEntityToLegacyProposalEntity);
  const { t } = useTranslation();

  const {
    mutateAsync,
    isPending: isMutating,
  } = useUpdateProposal(id);

  const { unlockNextStateAndNavigate } = useContext(ProposalCreationContext);
  const isPending = isLoading || isMutating;

  const form = useForm<ProposalCreationClientAndMortgageForm>({
    initialValues: formInitialValues(data) as ProposalCreationClientAndMortgageForm,
    validateInputOnChange: true,
    validate: zodResolver(clientAndMortgageUAESchema),
  });

  const onFormSubmit = async (formData: ProposalDetailsFormType) => {
    if (isPending) {
      return;
    }
    clearAmplitudeEventContext();

    await mutateAsync({
      mortgage: mapMortgageDetailsFormToRequest(formData.values),
      client: mapProposalClientDetailsFormToRequest(formData.values),
      property: mapPropertyDetailsFormToRequest(formData.values),
    });

    navigate({
      to: ProposalCreationBankProductsRoute.to,
      params: { id: data!.external_id },
    });
  };

  const onSubmit = async () => {
    const validationResult = form.validate();

    if (validationResult.hasErrors) {
      return;
    }

    if (form.isDirty()) {
      await onFormSubmit(form);
    }
    unlockNextStateAndNavigate();
  };

  return (
    <Flex direction='column' gap='6'>
      <Flex direction='column'>
        <Text size='2xl' weight='medium' color='text-primary'>{t('proposals.proposalsDetails.title')}</Text>
        <Text size='md' weight='regular' color='text-secondary'>{t('proposals.proposalsDetails.subtitle')}</Text>
      </Flex>

      {isLoading ? (
        <Flex mt={ rem(24) } direction='column'>
          <Flex gap={ rem(24) }>
            <Skeleton radius={ theme.radius.md } h={ rem(295) } />
            <Skeleton radius={ theme.radius.md } h={ rem(295) } />
          </Flex>
          <Flex mt={ rem(24) }>
            <Skeleton radius={ theme.radius.md } h={ rem(295) } />
          </Flex>
        </Flex>
      ) : (
        <Flex gap='6' direction='column'>
          <ClientDetails
            form={ form }
            isNameDisabled={ !!data?.client_external_id }
            isDisabled={ isPending }
          />
          <PropertyDetails
            form={ form }
            isDisabled={ isPending }
          />
          <MortgageDetails
            form={ form }
            isDisabled={ isPending }
          />
          <Flex w='100%' justifyContent='flex-end'>
            <Button
              disabled={ isPending }
              onClick={ () => onSubmit() }
              type='submit'
              loading={ isPending }
              variant={ isPending ? 'outline' : 'primary' }
              data-test='proposal-next-btn'
            >
              Next
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
